import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  signUp,
  signIn,
  signOut,
  resetPassword,
  getUserProfile,
  updateUserProfile,
  getCurrentUser,
  getCurrentSession,
} from "../lib/supabase";

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state on mount
  useEffect(() => {
    getSession();
  }, []);

  // Load user profile when user changes
  useEffect(() => {
    if (user?.id) {
      loadUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [user]);

  const getSession = async () => {
    try {
      setLoading(true);
      console.log("🔍 Getting current session...");

      // Check for stored token and user data
      const token = localStorage.getItem("auth_token");
      const userData = localStorage.getItem("user_data");

      if (token && userData) {
        const parsedUser = JSON.parse(userData);
        console.log("✅ Found stored session for user:", parsedUser.email);

        setUser(parsedUser);
        setSession({ access_token: token });

        // Verify token is still valid by getting current user
        const { data: currentUser, error } = await getCurrentUser();
        if (error || !currentUser) {
          console.log("⚠️ Stored token is invalid, clearing session");
          await signout();
        } else {
          console.log("✅ Session verified successfully");
          setUser(currentUser);
        }
      } else {
        console.log("📭 No stored session found");
        setUser(null);
        setSession(null);
      }
    } catch (error) {
      console.error("Session error:", error);
      setUser(null);
      setSession(null);
      // Clear invalid stored data
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_data");
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfile = async () => {
    try {
      console.log("👤 Loading user profile...");
      const { data: profile, error } = await getUserProfile();

      if (error) {
        console.error("Profile loading error:", error);
        toast.error("Failed to load user profile");
        return;
      }

      if (profile) {
        console.log("✅ User profile loaded successfully");
        setUserProfile(profile);
      }
    } catch (error) {
      console.error("Profile loading error:", error);
      toast.error("Failed to load user profile");
    }
  };

  const signup = async (email, password, fullName) => {
    setLoading(true);
    try {
      console.log("📝 Signing up user:", email);

      const { data, error } = await signUp(email, password, fullName);

      if (error) {
        throw error;
      }

      console.log("✅ User created successfully:", data);

      // Store authentication data
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("user_data", JSON.stringify(data.user));

      setUser(data.user);
      setSession({ access_token: data.token });

      toast.success("Account created successfully!");
      return data.user;
    } catch (error) {
      console.error("Signup error:", error);
      toast.error(error.message || "Failed to create account");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signin = async (email, password) => {
    setLoading(true);
    try {
      console.log("🔐 Signing in user:", email);

      const { data, error } = await signIn(email, password);

      if (error) {
        throw error;
      }

      console.log("✅ User signed in successfully:", data);

      // Store authentication data
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("user_data", JSON.stringify(data.user));

      setUser(data.user);
      setSession({ access_token: data.token });

      toast.success("Welcome back!");
      return data.user;
    } catch (error) {
      console.error("Signin error:", error);
      toast.error(error.message || "Failed to sign in");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signout = async () => {
    setLoading(true);
    try {
      console.log("👋 Signing out user");

      await signOut();

      // Clear state
      setUser(null);
      setSession(null);
      setUserProfile(null);

      // Clear local storage
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_data");

      console.log("✅ User signed out successfully");
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Signout error:", error);
      toast.error("Failed to sign out");
    } finally {
      setLoading(false);
    }
  };

  const resetUserPassword = async (email) => {
    try {
      console.log("🔄 Requesting password reset for:", email);

      const { data, error } = await resetPassword(email);

      if (error) {
        throw error;
      }

      console.log("✅ Password reset email sent");
      toast.success("Password reset instructions sent to your email");
      return true;
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error(error.message || "Failed to send reset email");
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    try {
      console.log("📝 Updating user profile:", updates);

      const { data: updatedUser, error } = await updateUserProfile(
        user.id,
        updates
      );

      if (error) {
        throw error;
      }

      console.log("✅ Profile updated successfully");

      // Update local state
      setUser(updatedUser);
      setUserProfile(updatedUser.profile);

      // Update stored user data
      localStorage.setItem("user_data", JSON.stringify(updatedUser));

      toast.success("Profile updated successfully");
      return updatedUser;
    } catch (error) {
      console.error("Update profile error:", error);
      toast.error(error.message || "Failed to update profile");
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    session,
    loading,
    signup,
    signin,
    signout,
    resetUserPassword,
    updateProfile,
    loadUserProfile,
    getSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
